/**
 * Maps a notification to a dashboard action (route + tab + refresh key).
 *
 * On click: caller navigates to `path` (with `?tab=<tab>&nrk=<refreshKey>`
 * appended so dashboards switch tab AND silently refetch their data),
 * optionally scrolls to `scrollId`, and dispatches `notification-refresh`.
 *
 * On poll, when the polling layer detects new (unseen) notifications, it
 * dispatches `notification-refresh` carrying the union of refresh keys so any
 * mounted dashboard listening for that key can silently refetch — no
 * full-page reload, no loading spinner, just updated data in place.
 */

const WORKER = 'worker';
const EMPLOYER = 'employer';
const MEDICAL = 'medical';
const ADMIN = 'admin';

const map = {
    [WORKER]: {
        offer: { path: '/worker', refreshKey: 'offers', scrollId: 'offers-section' },
        offer_response: { path: '/worker', refreshKey: 'offers', scrollId: 'offers-section' },
        interview: { path: '/worker', refreshKey: 'interviews', scrollId: 'interviews-section' },
        visa_update: { path: '/worker', refreshKey: 'visa', scrollId: 'visa-tracking-section' },
        payment: { path: '/worker', refreshKey: 'visa', scrollId: 'visa-tracking-section' },
        medical: { path: '/worker', refreshKey: 'documents', scrollId: 'documents-section' },
        approval: { path: '/settings' }, // rejected worker -> profile settings (rare)
        application: { path: '/worker', tab: 'my-applications', refreshKey: 'applications', scrollId: 'my-applications-section' },
    },
    [EMPLOYER]: {
        offer_response: { path: '/employer', tab: 'candidates', refreshKey: 'candidates' },
        approval: { path: '/employer', tab: 'talent', refreshKey: 'me' }, // approved/rejected -> talent
        offer: { path: '/employer', tab: 'candidates', refreshKey: 'candidates' },
        interview: { path: '/employer', tab: 'interviews', refreshKey: 'interviews' },
        submission: { path: '/settings', section: 'submissions', refreshKey: 'submissions', scrollId: 'submissions-section' },
        application: { path: '/employer', tab: 'posted-jobs', refreshKey: 'posted-jobs' },
    },
    [MEDICAL]: {
        medical: { path: '/medical', tab: 'appointments', refreshKey: 'appointments' },
        info: { path: '/medical', tab: 'appointments', refreshKey: 'appointments' },
    },
    [ADMIN]: {
        approval: { path: '/admin', tab: 'users', refreshKey: 'users', filter: 'pending' },
        payment: { path: '/admin', tab: 'transactions', refreshKey: 'transactions' },
        info: { path: '/admin', tab: 'users', refreshKey: 'users' },
        submission: { path: '/admin', tab: 'submissions', refreshKey: 'submissions' },
    },
};

export function getNotificationAction(role, type) {
    return (map[role] && map[role][type]) || null;
}

/**
 * Build the query-string suffix for a notification action so the target
 * dashboard can read it on mount.
 */
export function buildActionUrl(action) {
    if (!action) return null;
    const params = new URLSearchParams();
    if (action.tab) params.set('tab', action.tab);
    if (action.refreshKey) params.set('nrk', action.refreshKey);
    if (action.filter) params.set('filter', action.filter);
    if (action.section) params.set('section', action.section);
    const q = params.toString();
    return action.path + (q ? `?${q}` : '');
}

export const REFRESH_EVENT = 'gulfbridge:notification-refresh';

/**
 * Dispatch the global refresh event with one or more refresh keys.
 * Dashboards listen via `window.addEventListener(REFRESH_EVENT, ...)`.
 */
export function dispatchRefresh(keys) {
    if (!keys || (Array.isArray(keys) && keys.length === 0)) return;
    const set = new Set(Array.isArray(keys) ? keys : [keys]);
    window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: { keys: Array.from(set) } }));
}
